import { CubicBezierCurve, Point, SimpleCubicBezierCurve } from '@@/bits/bezier_curve'
import { GeoMap, IconSequence, LatLng, LatLngLiteral, MapEventsManager, MapsEventListener, Polyline } from '@@/bits/map'
import MapConnectionLabel from '@@/bits/map_connection_label'

const HIGHLIGHTED_OPACITY = 0.5
class MapConnection {
  private points: Point[] = []
  private line!: Polyline
  private start!: LatLngLiteral
  private end!: LatLngLiteral
  private label!: string
  private labelType: 'regular' | 'count' = 'regular'
  private labelOverlayView?: MapConnectionLabel
  private map: GeoMap | null = null
  private bezierCurve!: CubicBezierCurve
  private resolution = 100
  private color = '#000'
  private isDeleteButtonShown = false
  private mouseOverListener?: MapsEventListener
  private mouseOutListener?: MapsEventListener
  private mouseClickListener?: MapsEventListener
  private deleteEventListener: (event: MouseEvent) => void = (): void => {}

  public constructor(
    start: LatLngLiteral,
    end: LatLngLiteral,
    label: string,
    resolution = 100,
    map: GeoMap | null = null,
  ) {
    this.start = start
    this.end = end
    this.label = label
    this.map = map
    this.resolution = resolution
    this.createLine()
    this.createLabel()
  }

  private getIconsForLine(opacity = 1): IconSequence[] {
    return [
      {
        icon: {
          path: 'M 0,-0.15 0,0.15', // draw stroke dash pixel by pixel 0.3*20=6
          strokeColor: this.color,
          strokeOpacity: opacity,
          strokeWeight: 2,
        },
        repeat: '12px', // 6px gap and 6px line
      },
      {
        icon: {
          fillColor: this.color,
          fillOpacity: 1,
          path: 'M -0.2,-0.6 0,0 0.2,-0.6 -0.2, -0.6', // backwards arrowhead put at the end location
          strokeColor: this.color,
          strokeOpacity: 0,
          strokeWeight: 1,
        },
        // the arrowhead is 35px from the end location in case the marker blocks the arrowhead
        offset: '35px',
      },
    ]
  }

  // Icons for the white x icon inside red circle
  // https://icons8.com/icon/63688/cancel
  // Fixed rotation is true so that the icon doesn't rotate with the curving of the connection curve
  private static getDeleteButton(opacity = 1): IconSequence[] {
    return [
      {
        icon: {
          path: 'M0.5 0c0 .276125 -.223875 .5 -.5 .5S-.5 .276125 -.5 0S-.276125 -.5 0 -.5S.5 -.276125 .5 0z', // the circle
          fillColor: '#f44336',
          fillOpacity: opacity,
        },
        offset: '50%',
        fixedRotation: true,
      },
      {
        icon: {
          path: 'M.1414 -.2121l0.0707 0.0707l-.3535 .3535l-.0707 -.0707L.1414 -.2121z', // slash
          fillColor: '#fff',
          fillOpacity: opacity,
        },
        offset: '50%',
        fixedRotation: true,
      },
      {
        icon: {
          path: 'M.2121 .1414l-.0707 .0707l-.3535 -.3535l.0707 -.0707L.2121 .1414z', // slash
          fillColor: '#fff',
          fillOpacity: opacity,
        },
        offset: '50%',
        fixedRotation: true,
      },
    ]
  }

  private getIconsWithDeleteButton(): IconSequence[] {
    return [...this.getIconsForLine(1), ...MapConnection.getDeleteButton(1)]
  }

  private getIconsWithDeleteButtonAndHighlightedStroke(): IconSequence[] {
    return [...this.getIconsForLine(HIGHLIGHTED_OPACITY), ...MapConnection.getDeleteButton(HIGHLIGHTED_OPACITY)]
  }

  private createLabel(): void {
    if (this.label) {
      this.labelOverlayView = new MapConnectionLabel({
        content: this.label,
        location: {
          lat: this.points[49].y,
          lng: this.points[49].x,
        },
        type: this.labelType,
      })
    }
  }

  private createPoints(): void {
    this.points = []
    // If the connection can cross the IDL for a much shorter
    // route, do it. Connection should not be > 4/5 map length
    if (this.start.lng - this.end.lng > 280) {
      this.end.lng += 360
    } else if (this.end.lng - this.start.lng > 280) {
      this.start.lng += 360
    }
    // draw the curve from the end to the start so that we can put the arrowhead at a pixel offset from the end location
    this.bezierCurve = new SimpleCubicBezierCurve(
      { x: this.end.lng, y: this.end.lat },
      { x: this.start.lng, y: this.start.lat },
    )
    for (let i = 0; i <= this.resolution; i += 1) {
      this.points.push(this.bezierCurve.getPointOnBezierCurve(i / this.resolution))
    }
  }

  private createLine(): void {
    this.createPoints()
    this.line = new Polyline({
      geodesic: true,
      icons: this.getIconsForLine(1),
      path: this.points.map((p: Point): LatLng => new LatLng(p.y, p.x)),
      strokeColor: this.color,
      strokeOpacity: 0,
      strokeWeight: 20,
    })
  }

  public addDeleteEventListener(callback: (event: MouseEvent) => void): boolean {
    this.deleteEventListener = callback
    return true
  }

  public setLocation(start: LatLngLiteral, end: LatLngLiteral): void {
    if (
      this.start.lat === start.lat &&
      this.start.lng === start.lng &&
      this.end.lat === end.lat &&
      this.end.lng === end.lng
    )
      return
    this.start = start
    this.end = end
    this.createPoints()
    this.line.setMap(null)
    this.line.setPath(this.points.map((p: Point): LatLng => new LatLng(p.y, p.x)))
    this.line.setMap(this.map || null)
    if (!this.labelOverlayView) return
    // this.labelOverlayView.setMap(null)
    this.labelOverlayView.setLocation({
      lat: this.points[49].y,
      lng: this.points[49].x,
    })
  }

  public setLabel(label: string): void {
    this.label = label
    if (!label) {
      if (this.labelOverlayView) {
        this.labelOverlayView.remove()
      }
      return
    }
    if (!this.labelOverlayView) {
      this.createLabel()
    }
    if (!this.labelOverlayView) return
    this.map && this.labelOverlayView.show(this.map)
    this.labelOverlayView.setType(this.labelType)
    this.labelOverlayView.setContent(label)
  }

  public setLabelType(type: 'regular' | 'count' = 'regular'): void {
    this.labelType = type
    this.labelOverlayView && this.labelOverlayView.setType(type)
  }

  public setLabelColor({
    backgroundColor,
    foregroundColor,
  }: {
    backgroundColor: string
    foregroundColor: string
  }): void {
    this.labelOverlayView &&
      this.labelOverlayView.setColor({
        backgroundColor,
        foregroundColor,
      })
  }

  public setMap(map: GeoMap | null): void {
    this.line.setMap(null)
    this.map = map
    this.line.setMap(this.map)
    this.labelOverlayView && this.labelOverlayView.show(this.map)
    this.labelOverlayView && this.labelOverlayView.setType(this.labelType)
    // this.showControlLines()
  }

  public setColor(color: string): void {
    this.color = color
    this.line.setOptions({
      strokeColor: color,
    })
    this.isDeleteButtonShown ? this.showDeleteButton() : this.hideDeleteButton()
  }

  public showDeleteButton(): void {
    // Used http://petercollingridge.appspot.com/svg-transforms to scale down and center the svg around 0,0
    // <path fill="#f44336" d="M5 0c0 2.76125 -2.23875 5 -5 5S-5 2.76125 -5 0S-2.76125 -5 0 -5S5 -2.76125 5 0z"/>
    // <path fill="#fff" d="M1.414 -2.121l0.707 0.707l-3.535 3.535l-0.707 -0.707L1.414 -2.121z"/>
    // <path fill="#fff" d="M2.121 1.414l-0.707 0.707l-3.535 -3.535l0.707 -0.707L2.121 1.414z"/>
    this.isDeleteButtonShown = true
    this.labelOverlayView && this.labelOverlayView.hide()
    this.line.setOptions({
      icons: this.getIconsWithDeleteButton(),
    })
    this.mouseOverListener = MapEventsManager.addListener(this.line, 'mouseover', (): void => {
      this.line.setOptions({
        icons: this.getIconsWithDeleteButtonAndHighlightedStroke(),
      })
    })
    this.mouseOutListener = MapEventsManager.addListener(this.line, 'mouseout', (): void => {
      this.line.setOptions({
        icons: this.getIconsWithDeleteButton(),
      })
    })
    this.mouseClickListener = MapEventsManager.addListener(this.line, 'click', this.deleteEventListener)
  }

  public hideDeleteButton(): void {
    this.isDeleteButtonShown = false
    this.labelOverlayView && this.labelOverlayView.show(this.map)
    this.line.setOptions({
      icons: this.getIconsForLine(1),
    })
    this.mouseOverListener && this.mouseOverListener.remove()
    this.mouseOutListener && this.mouseOutListener.remove()
    this.mouseClickListener && this.mouseClickListener.remove()
  }

  // For debugging purpose
  public showControlLines(): void {
    const cp1 = this.bezierCurve.controlPoints[0]
    // draw the bezier control line from cp1 to starting vertex
    const plcp1 = new Polyline({
      path: [new LatLng(cp1.y, cp1.x), new LatLng(this.points[0].y, this.points[0].x)],
      strokeColor: 'rgba(0,255,0,0.5)',
      strokeWeight: 2,
    })
    const cp2 = this.bezierCurve.controlPoints[1]
    // draw the bezier control line from cp2 to ending vertex
    const plcp2 = new Polyline({
      path: [
        new LatLng(cp2.y, cp2.x),
        new LatLng(this.points[this.points.length - 1].y, this.points[this.points.length - 1].x),
      ],
      strokeColor: 'rgba(0,0,255,0.5)',
      strokeWeight: 2,
    })
    plcp1.setMap(this.map)
    plcp2.setMap(this.map)
  }

  public destroy(): void {
    this.line.setMap(null)
    this.mouseOverListener && this.mouseOverListener.remove()
    this.mouseOutListener && this.mouseOutListener.remove()
    this.mouseClickListener && this.mouseClickListener.remove()
    this.labelOverlayView && this.labelOverlayView.remove()
    // Handle edge cases where sometimes labelOverlayView
    // doesn't remove on zoom in and out
    setTimeout((): void => {
      this.labelOverlayView && this.labelOverlayView.remove()
    }, 0)
  }
}

export { MapConnection }
